import React, { useState, useEffect } from 'react';
import { Avatar, Box, Typography, keyframes, useTheme } from '@mui/material';
import { getCurrentUser, fetchUserAttributes, signOut } from '@aws-amplify/auth';
import { stringAvatar } from '../../utils/AvatarUtils';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const LIGHT_PALETTE = {
  surface: '#ffffff',
  border: '#e4e4e7',
  textPrimary: '#18181b',
  textSecondary: '#71717a',
};

const DARK_PALETTE = {
  surface: '#1a1a1e',
  border: '#27272a',
  textPrimary: '#fafafa',
  textSecondary: '#a1a1aa',
};

/**
 * Shared authenticated-user chip: avatar + email + logout.
 *
 * @param {object}  [palette]  Colour tokens (surface, border, textPrimary, textSecondary).
 *                              Falls back to theme-aware light/dark palette.
 * @param {object}  [sx]       Sx override for the outer wrapper (positioning, z-index, etc.).
 */
export default function AuthenticatedUserChip({ palette: paletteProp, sx: sxProp } = {}) {
  const theme = useTheme();
  const palette = paletteProp || (theme.palette.mode === 'dark' ? DARK_PALETTE : LIGHT_PALETTE);
  const [email, setEmail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await getCurrentUser();
        const attrs = await fetchUserAttributes();
        if (!cancelled) setEmail(attrs.email || attrs.preferred_username || 'User');
      } catch {
        // Not authenticated
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!email) return null;

  const handleLogout = async () => {
    try { await signOut(); } catch { /* ignore */ }
    window.location.reload();
  };

  const avatar = stringAvatar(email);

  return (
    <Box
      sx={{
        animation: `${fadeIn} 300ms ease`,
        ...sxProp,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          bgcolor: `${palette.surface}cc`,
          border: `1px solid ${palette.border}`,
          borderRadius: '8px',
          px: 0.75,
          py: 0.5,
          backdropFilter: 'blur(8px)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 1px 2px rgba(0,0,0,0.2)'
            : '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <Avatar
          variant="rounded"
          {...avatar}
          sx={{
            ...avatar.sx,
            width: 20,
            height: 20,
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 600,
          }}
        />

        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 450,
            color: palette.textSecondary,
            maxWidth: 150,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1,
          }}
        >
          {email}
        </Typography>

        <Box sx={{ width: '1px', height: 12, bgcolor: palette.border, flexShrink: 0 }} />

        <Box
          onClick={handleLogout}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLogout(); }}
          sx={{
            fontSize: '11px',
            fontWeight: 500,
            color: palette.textSecondary,
            cursor: 'pointer',
            borderRadius: '4px',
            px: 0.75,
            py: 0.25,
            lineHeight: 1,
            userSelect: 'none',
            transition: 'color 150ms ease',
            '&:hover': {
              color: palette.textPrimary,
            },
          }}
        >
          Log out
        </Box>
      </Box>
    </Box>
  );
}
